import { PlusIcon } from "@heroicons/react/24/solid";
import { countWords } from "../../app/utils";
import StackedDate from "../../components/StackedDate";
import PromptPanel from "../prompts/PromptPanel";
import CommentedEssay from "../writing/drafting/CommentedEssay";
import Messages from "../messages/Messages";
import WritingAnalysis from "./WritingAnalysis";
import PlagiarismAnalysis from "./PlagiarismAnalysis";
import { DraftList, PeerReviews } from "./ScoreReport";
import { ScoreReportViewModel } from "./scoreReportApiSlice";

interface Props {
	scoreReports?: ScoreReportViewModel[];
}
export default function PrintLastScoreReports(props: Props) {
	const getPageMargins = () => {
		return `@page { margin: 40px 50px 40px 50px !important; }`;
	};

	const getAnalysis = (scoreReport: ScoreReportViewModel) => {
		return {
			id: scoreReport.draftScore?.id,
			showScore: scoreReport.draftScore !== null,
			genre: scoreReport.prompt.genre.name,
			categories: scoreReport.draftScore?.categoryScores,
			cSharpScoreFormat: scoreReport.draftScore?.cSharpScoreFormat,
			decimalPlacesDisplayed: scoreReport.draftScore?.decimalPlacesDisplayed,
			canScoreProctorScorableCategories: scoreReport.canScoreProctorScorableCategories,
			staticFeedback: scoreReport.staticFeedback,
			targetedFeedback: scoreReport.targetedFeedback,
			llmFeedback: scoreReport.llmFeedback,
			feedbackTutorials: scoreReport.feedbackTutorials ?? [],
		};
	};

	return (
		<div>
			<style>{getPageMargins()}</style>
			{props.scoreReports?.map((scoreReport) => (
				<section className="break-after-page">
					<>
						<section>
							<h1>Score Report</h1>
							<div className="flex">
								<StackedDate
									className="ml-8 mr-24"
									date={scoreReport.draft.createDate}
								/>
								<div>
									<h2>{scoreReport.prompt.title}</h2>
									<div>by {scoreReport.writerName}</div>
									<div>for {scoreReport.course.name}</div>
								</div>
							</div>
							<div>
								<PromptPanel
									name={scoreReport.prompt.title}
									showName={false}
									renderedText={scoreReport.prompt.renderedText}
									stimulusMaterials={scoreReport.stimulusMaterials}
									showGenre={false}></PromptPanel>
							</div>
						</section>
						<section className="flex mt-8">
							<div className="w-3/4">
								<CommentedEssay
									commentedEssay={scoreReport.commentedEssay}
									isScoreReport={true}
								/>
							</div>
							<div className="w-1/4 ml-8">
								<div>
									<div className="mt-8 text-xl font-medium">
										Word Count:&nbsp;
										<span id="word-count" className="font-normal">
											{countWords(scoreReport.pureTextForWordCount)}
										</span>
									</div>

									<div className="mb-6">
										<h2 className="mt-6">Submissions</h2>

										<div className="pb-2">
											{scoreReport.remainingRevisions} submissions left
										</div>
										<DraftList
											drafts={scoreReport.drafts}
											currentDraft={scoreReport.draft.draftNumber}
											status={scoreReport.draft.status}
											draftsWithReviews={scoreReport.reviewedTestIds}
											showAll={false}
											hasLegacyHtmlFormat={
												scoreReport.draft.hasLegacyHtmlFormat
											}
											onChangeDraft={() => {}}
										/>
										{scoreReport.drafts.length > 5 && (
											<div className="mt-1 text-black">
												<span className="relative flex items-center font-medium">
													<PlusIcon className="h-4 inline mr-0 " />
													&nbsp;Show All
												</span>
											</div>
										)}
									</div>

									{scoreReport.showPeerReviewSection && (
										<>
											<h2 className="mt-8">Peer Review</h2>

											{scoreReport.totalRequestedReviewCount == 0 &&
												!scoreReport.isWriter && <p>Not requested</p>}
											{scoreReport.totalRequestedReviewCount > 0 && (
												<>
													<p>Sent {scoreReport.dateSent}</p>
													<p>
														{scoreReport.reviewCount} of{" "}
														{scoreReport.totalRequestedReviewCount}{" "}
														complete
													</p>
												</>
											)}
										</>
									)}
								</div>
							</div>
						</section>
						{scoreReport.showPeerReviewSection &&
							scoreReport.completedReviews.length > 0 && (
								<>
									<hr className="my-4" />
									<PeerReviews
										draftId={scoreReport.draft.id}
										reviews={scoreReport.completedReviews}
										expandAllByDefault={true}
									/>
								</>
							)}
						<hr className="my-4" />
						<section>
							<h1>Scores</h1>
							<div className="flex mt-4">
								<div>
									<p>
										This essay’s total score is{" "}
										<span className="text-xl ml-2 font-semibold text-primary-600">
											{scoreReport.draftScore.totalScore}
										</span>
									</p>
									{scoreReport.showNormativeScores && (
										<div>
											<p className="mt-4">
												Percentile Rank is{" "}
												<span className="text-lg bold ml-2">
													{scoreReport.draftScoreNorm.percentileRank}
												</span>
											</p>
											<p>
												Stanine is{" "}
												<span className="text-lg bold ml-2">
													{scoreReport.draftScoreNorm.stanine}
												</span>
											</p>
										</div>
									)}
									<p className="mt-4">
										The essay was scored by PEG, the automated essay scorer,
										according to the scoring standard for your level.
									</p>
								</div>
							</div>
						</section>
						<hr className="my-4" />
						<section>
							<WritingAnalysis
								analysis={getAnalysis(scoreReport)}
								onProctorScore={() => {}}
							/>
						</section>
						<hr className="my-4" />
						{scoreReport.plagiarismAnalysis != null && (
							<>
								<section>
									<PlagiarismAnalysis analysis={scoreReport.plagiarismAnalysis} />
								</section>
								<hr className="my-4" />
							</>
						)}
						<section>
							<Messages draftId={scoreReport.draft.id} />
						</section>
					</>
				</section>
			))}
		</div>
	);
}
